import { message } from '@repo/ui';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Checkbox, Form, InputNumber, Select, Space, TimePicker, Tooltip,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import AutomationsCriteriaBuilder from 'automations/components/Nodes/AutomationsCriteriaBuilder';
import TimeBufferInput, { type TimeBufferValue } from 'automations/components/Shared/TimeBufferInput';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { useGetAutomationAiCallAgentsQuery } from 'automations/services/aiCallAgentsService';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type TriggerAiCallNodeData, type TriggerAiCallNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { saveActionNode } from 'automations/utils/actionNode';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import styles from './TriggerAiCallContent.module.scss';

type TriggerAiCallConfig = TriggerAiCallNodeData['additionalOptions']['triggerAiCall'];
type RetryGapUnit = 'minutes' | 'hours';

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const RETRY_GAP_UNIT_OPTIONS: { value: RetryGapUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
];

export default function TriggerAiCallContent({
  node,
}: {
  node: NodeProps<TriggerAiCallNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const parentId = useSelector(getParentIdSelector(node.id));
  const cfg: Partial<TriggerAiCallConfig> | undefined = node.data?.additionalOptions?.triggerAiCall;

  const { data, isLoading } = useGetAutomationAiCallAgentsQuery();
  const [agentUuid, setAgentUuid] = useState<string | undefined>(cfg?.agentUuid);
  const [skipCriteriaGroupId, setSkipCriteriaGroupId] = useState<number | undefined>(
    cfg?.skipCriteriaGroupId,
  );
  const [criteriaOpen, setCriteriaOpen] = useState(false);

  const [maxRetries, setMaxRetries] = useState<number | null>(cfg?.maxRetries ?? null);
  const [retryGapValue, setRetryGapValue] = useState<number | null>(cfg?.retryGap?.value ?? null);
  const [retryGapUnit, setRetryGapUnit] = useState<RetryGapUnit>(cfg?.retryGap?.unit ?? 'minutes');

  const [schedule, setSchedule] = useState<TimeBufferValue | undefined>(cfg?.schedule);

  const [callingDays, setCallingDays] = useState<number[]>(cfg?.callingWindow?.days ?? []);
  const [windowRange, setWindowRange] = useState<[Dayjs, Dayjs] | null>(
    cfg?.callingWindow
      ? [dayjs(cfg.callingWindow.startTime, 'HH:mm'), dayjs(cfg.callingWindow.endTime, 'HH:mm')]
      : null,
  );

  const [requireOwnerLeadTrigger, setRequireOwnerLeadTrigger] = useState<boolean>(
    cfg?.requireOwnerLeadTrigger ?? false,
  );

  // This component holds the toggle state above the drawer, so `destroyOnClose` re-mounts only the
  // form, not this state, and the `useState` initializer does not re-run when the node's saved config
  // changes underneath a mounted instance. Re-sync when the persisted value changes so a stale `false`
  // can't silently drop an already-saved owner-eligibility requirement on the next save. Depends on the
  // persisted value only, so an in-progress (unsaved) toggle is never clobbered.
  useEffect(() => {
    setRequireOwnerLeadTrigger(cfg?.requireOwnerLeadTrigger ?? false);
  }, [cfg?.requireOwnerLeadTrigger]);

  const agentOptions = (data?.data ?? [])
    .filter((agent) => agent.active)
    .map((agent) => ({ value: agent.agent_uuid, label: agent.name }));

  const handleSave = useCallback(async () => {
    if (!agentUuid) {
      message.error('Please select an agent');
      return;
    }

    try {
      const triggerAiCall: any = { agentUuid, skipCriteriaGroupId };

      if ((maxRetries ?? 0) > 0) {
        triggerAiCall.maxRetries = maxRetries;
        triggerAiCall.retryGap = { value: retryGapValue, unit: retryGapUnit };
      }

      if (schedule?.dependsOn && schedule?.value && schedule?.unit) {
        triggerAiCall.schedule = schedule;
      }

      if (callingDays.length && windowRange?.[0] && windowRange?.[1]) {
        triggerAiCall.callingWindow = {
          days: callingDays,
          startTime: windowRange[0].format('HH:mm'),
          endTime: windowRange[1].format('HH:mm'),
        };
      }

      if (requireOwnerLeadTrigger) {
        triggerAiCall.requireOwnerLeadTrigger = true;
      }

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: { triggerAiCall },
      }));
      await dispatch(saveActionNode(node.id, parentId, contentMode));
      setMenuOpen(false);
    } catch (error: any) {
      message.error(error?.data?.message ?? 'Something Went Wrong Updating Node');
    }
  }, [
    agentUuid, skipCriteriaGroupId, maxRetries, retryGapValue, retryGapUnit, schedule, callingDays, windowRange,
    requireOwnerLeadTrigger, contentMode, dispatch, node.id, parentId, setMenuOpen,
  ]);

  let saveDisabledReason = '';
  if (!agentUuid) {
    saveDisabledReason = 'Select an AI agent to continue';
  } else if ((maxRetries ?? 0) > 0 && !retryGapValue) {
    saveDisabledReason = 'Enter a retry gap value (required when max retries is set)';
  }

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: contentMode === 'create' ? 'Trigger AI Call' : 'Edit AI Call',
        destroyOnClose: true,
      }}
    >
      <Card title={contentMode === 'create' ? 'Set Action' : 'Edit Action'}>
        <Form layout="vertical">
          <Form.Item label="AI agent" required>
            <Select
              loading={isLoading}
              placeholder="Select an AI agent"
              options={agentOptions}
              value={agentUuid}
              onChange={setAgentUuid}
              showSearch
              optionFilterProp="label"
              className={styles.select}
            />
          </Form.Item>

          <Form.Item label="Skip conditions (optional)">
            <Space>
              <Button onClick={() => setCriteriaOpen(true)}>
                {skipCriteriaGroupId ? 'Edit skip conditions' : 'Add skip conditions'}
              </Button>
              {skipCriteriaGroupId ? <span>Configured</span> : <span>None — call always placed</span>}
            </Space>
          </Form.Item>

          <Form.Item label="Max retries">
            <InputNumber min={0} style={{ width: '100%' }} value={maxRetries} onChange={setMaxRetries} />
          </Form.Item>

          {(maxRetries ?? 0) > 0 && (
            <Form.Item label="Retry gap">
              <Space.Compact block>
                <InputNumber
                  min={1}
                  placeholder="e.g. 30"
                  value={retryGapValue}
                  onChange={setRetryGapValue}
                  style={{ width: '70%' }}
                />
                <Select
                  value={retryGapUnit}
                  onChange={setRetryGapUnit}
                  options={RETRY_GAP_UNIT_OPTIONS}
                  style={{ width: '30%' }}
                />
              </Space.Compact>
            </Form.Item>
          )}

          <Form.Item label="Call at a date &amp; time on the lead (optional)">
            <Space direction="vertical" className={styles.select}>
              <TimeBufferInput
                value={schedule}
                onChange={setSchedule}
                anchorPlaceholder="Date &amp; time field"
              />
              <span className={styles.helperText}>
                Leave empty to call as soon as the automation reaches this step. The calling window
                below still applies, so a call bound to an out-of-hours time moves to the next slot.
              </span>
            </Space>
          </Form.Item>

          <Form.Item label="Calling window (optional)">
            <Space direction="vertical" className={styles.select}>
              <Select
                mode="multiple"
                placeholder="Any day"
                options={WEEKDAY_OPTIONS}
                value={callingDays}
                onChange={setCallingDays}
                className={styles.select}
              />
              <TimePicker.RangePicker
                format="HH:mm"
                minuteStep={15}
                value={windowRange}
                onChange={(value) => setWindowRange(value as [Dayjs, Dayjs] | null)}
                className={styles.select}
              />
              <span className={styles.helperText}>
                Calls outside this window are scheduled for the next allowed slot.
              </span>
            </Space>
          </Form.Item>

          <Form.Item>
            <Checkbox
              checked={requireOwnerLeadTrigger}
              onChange={(e) => setRequireOwnerLeadTrigger(e.target.checked)}
            >
              Only call when the lead owner has AI calling enabled
            </Checkbox>
            <div className={styles.helperText}>
              Leads whose owner does not have the AI calling feature are skipped.
            </div>
          </Form.Item>
        </Form>
      </Card>

      <Tooltip title={saveDisabledReason}>
        <Button
          type="primary"
          className={styles.saveBtn}
          onClick={handleSave}
          disabled={!!saveDisabledReason}
        >
          Save
        </Button>
      </Tooltip>

      <AutomationsCriteriaBuilder
        title="Skip conditions — do not call when these match"
        open={criteriaOpen}
        onClose={() => setCriteriaOpen(false)}
        existingCriteriaId={skipCriteriaGroupId}
        onSave={(criteriaId: number | undefined) => {
          setSkipCriteriaGroupId(criteriaId);
          setCriteriaOpen(false);
        }}
        saveButtonText="Set skip conditions"
      />
    </NodeMenu>
  );
}
